import tasksEn from "./en.tasks";
import bugsEn from "./en.bugs";
import notifyEn from "./en.notify";
import analyticsEn from "./en.analytics";
import workEn from "./en.work";
import sprintsEn from "./en.sprints";

const en = {
  ...tasksEn,
  ...bugsEn,
  ...notifyEn,
  ...analyticsEn,
  ...workEn,
  ...sprintsEn,
  editor: {
    formatting: "Formatting",
    bold: "Bold",
    italic: "Italic",
    strike: "Strikethrough",
    code: "Code",
    link: "Link",
    linkAddress: "Link address",
    apply: "Apply",
    bulletList: "Bulleted list",
    orderedList: "Numbered list",
    taskList: "Checklist",
    quote: "Quote",
    codeBlock: "Code block",
  },
  common: {
    bot: "bot",
    botHint: "An integration account (bot), not a person",
    dismiss: "Dismiss",
    clear: "Clear",
    more: "More actions",
    save: "Save",
    add: "Add",
    delete: "Delete",
    saving: "Saving…",
    cancel: "Cancel",
    close: "Close",
    create: "Create",
    edit: "Edit",
    remove: "Remove",
    copy: "Copy",
    copied: "Copied",
    download: "Download",
    loading: "Loading…",
    continue: "Continue",
    back: "Back",
    all: "All",
    optional: "optional",
    actions: "Actions",
    never: "Never",
    retry: "Try again",
    loadFailed: "Couldn't load this. Check your connection and try again.",
  },
  viewer: {
    zoomIn: "Zoom in",
    zoomOut: "Zoom out",
    fit: "Fit to screen",
    previous: "Previous",
    next: "Next",
    openOriginal: "Open original",
    counter: "{{n}} of {{total}}",
    hint: "Scroll or pinch to zoom · drag to move · double-click to zoom in or out · ← → to browse",
  },
  combobox: {
    create: "Create “{{value}}”",
  },
  roles: {
    manager: "Manager",
    developer: "Developer",
    tester: "Tester",
    viewer: "Viewer",
  },
  locales: {
    en: "English",
    uz: "Oʻzbekcha",
    ru: "Русский",
  },
  theme: {
    light: "Light",
    dark: "Dark",
    system: "System",
  },
  errors: {
    unauthorized: "Your session has ended. Please sign in again.",
    forbidden: "You don't have permission to do that.",
    csrf: "The request was blocked for security reasons. Reload the page and try again.",
    not_found: "Not found.",
    validation: "Some fields are invalid. Check them and try again.",
    conflict: "That already exists.",
    invalid_credentials: "Wrong username or password.",
    account_locked: "Too many failed attempts. The account is locked for 15 minutes.",
    account_disabled: "This account is disabled. Ask a manager.",
    invalid_code: "That code is not valid. Check it and try again.",
    owner_protected: "The owner account can only be changed by its owner.",
    storage_unavailable: "File storage is unavailable right now. Try again later.",
    internal: "Something went wrong on our side. Try again.",
    network: "Can't reach the server. Check your connection.",
  },
  auth: {
    signInTitle: "Sign in to Mixla",
    signInHint: "Use the username and password your manager gave you.",
    username: "Username",
    password: "Password",
    signIn: "Sign in",
    language: "Language",
    steps: {
      password: "Password",
      authenticator: "Authenticator",
      recovery: "Recovery codes",
    },
    verifyTitle: "Two-factor authentication",
    verifyHint: "Enter the 6-digit code from your authenticator app.",
    useRecovery: "Use a recovery code instead",
    useAuthenticator: "Use the authenticator app instead",
    recoveryLabel: "Recovery code",
    recoveryHint: "Each recovery code works only once.",
    verify: "Verify",
    otherAccount: "Sign in with another account",
    setupTitle: "Set up two-factor authentication",
    setupHint: "Required for every account. Scan the code with Google Authenticator, 1Password or any TOTP app.",
    cantScan: "Can't scan? Enter this key",
    copyKey: "Copy key",
    enterCode: "Enter the 6-digit code",
    verifyAndContinue: "Verify and continue",
    qrLabel: "QR code for your authenticator app",
    recoveryTitle: "Save your recovery codes",
    recoveryIntro:
      "If you lose your phone, each of these codes lets you sign in once. Store them somewhere safe — they won't be shown again.",
    copyAll: "Copy all",
    downloadTxt: "Download .txt",
    savedCheckbox: "I have saved these codes",
    finish: "Continue to Mixla",
    digit: "Digit {{n}} of 6",
    recoveryFileHeader: "Mixla recovery codes for {{username}}. Each code works once.",
  },
  nav: {
    apiDocs: "API docs",
    main: "Main",
    projects: "Projects",
    inbox: "Inbox",
    tasks: "Tasks",
    project: "Project",
    overview: "Overview",
    planning: "Planning",
    reports: "Reports",
    members: "Members",
    settings: "Settings",
    admin: "Admin",
    users: "Users",
    profile: "Profile",
    switchProject: "Switch project",
    allProjects: "All projects",
    noProjects: "No projects yet",
    openMenu: "Open menu",
  },
  projects: {
    title: "Projects",
    newProject: "New project",
    showArchived: "Show archived",
    emptyTitle: "No projects yet",
    emptyManager: "Create the first project, then add people to it.",
    emptyMember: "You're not in any project yet. Ask a manager to add you.",
    archived: "Archived",
    membersCount: "Members: {{count}}",
    yourRole: "Your role",
    key: "Key",
    keyHint: "2–10 characters: uppercase letters and digits, starting with a letter. It prefixes task keys (PAY-12). Can't be changed later.",
    name: "Name",
    description: "Description",
    created: "Project created",
  },
  overview: {
    majorBugs: "Open S1/S2 bugs",
    openBugs: "Open bugs in total: {{count}}",
    overdueBugs: "Overdue bugs",
    toTest: "To test",
    toFix: "To fix",
    openTasks: "Open tasks",
    overdueTasks: "Overdue tasks",
    bugsTitle: "Bugs needing attention",
    allBugs: "All bugs",
    tasksTitle: "Tasks waiting",
    allTasks: "All tasks",
    peopleTitle: "Who has what",
    roundsTitle: "Test rounds this week",
    noOpenBugs: "No open bugs.",
    nothingWaiting: "Nothing is waiting.",
    noRounds: "No rounds finished in the last 7 days.",
    person: "Person",
    bugsOwned: "Bugs",
    longestWait: "Longest wait",
    noDescription: "No description yet.",
    archivedNotice: "This project is archived. It's hidden from the project list until a manager unarchives it.",
  },
  members: {
    title: "Members",
    add: "Add member",
    user: "User",
    platformRole: "Platform role",
    projectRole: "Role in this project",
    sameAsPlatform: "Same as platform role",
    removeTitle: "Remove {{name}} from {{project}}?",
    removeBody: "They lose access to this project. Their history stays.",
    added: "Member added",
    removed: "Member removed",
    updated: "Role updated",
    empty: "No members yet.",
    readOnly: "Only project managers can change members.",
    noUsersLeft: "Everyone is already a member.",
    disabled: "Disabled",
    managerAlways: "Platform managers are managers in every project.",
    pickUser: "Choose a user",
  },
  settings: {
    title: "Project settings",
    general: "General",
    workflow: "Workflow",
    templates: "Templates",
    logo: "Logo",
    logoHint: "PNG, JPEG or WebP, up to 2 MB.",
    uploadLogo: "Upload logo",
    replaceLogo: "Replace",
    removeLogo: "Remove logo",
    uploading: "Uploading…",
    logoUploaded: "Logo updated",
    logoRemoved: "Logo removed",
    logoTooBig: "The file is larger than 2 MB.",
    logoBadType: "Use a PNG, JPEG or WebP image.",
    saved: "Settings saved",
    archiveSection: "Archive",
    archiveBody: "Archived projects are hidden from the project list. You can unarchive at any time; nothing is deleted.",
    archive: "Archive project",
    unarchive: "Unarchive project",
    archiveTitle: "Archive {{name}}?",
    archivedToast: "Project archived",
    unarchivedToast: "Project unarchived",
    forbidden: "Only project managers can change settings.",
  },
  users: {
    title: "Users",
    newUser: "New user",
    filterRole: "Role",
    filterStatus: "Status",
    allRoles: "All roles",
    allStatuses: "All statuses",
    active: "Active",
    disabled: "Disabled",
    name: "Name",
    username: "Username",
    role: "Role",
    twoFactor: "2FA",
    status: "Status",
    projects: "Projects",
    lastLogin: "Last sign-in",
    twoFactorOn: "Set up",
    twoFactorOff: "Not yet",
    owner: "Owner",
    you: "you",
    reset: "Reset password & 2FA",
    resetTitle: "Reset password and 2FA for {{name}}?",
    resetBody:
      "A new password is generated, two-factor authentication is cleared and all their sessions are signed out. They set up 2FA again at their next sign-in.",
    resetConfirm: "Reset",
    createTitle: "New user",
    displayName: "Display name",
    usernameHint: "Lowercase letters, digits, dots, dashes and underscores.",
    language: "Language",
    projectsLabel: "Projects",
    projectsHint: "Tick the projects this user joins. You can give them a different role in a project.",
    noProjectsYet: "No projects yet — you can add the user to projects later.",
    roleInProject: "Role in project",
    createButton: "Create user",
    created: "User created",
    passwordTitle: "Password for {{username}}",
    passwordWarning: "Copy it now and share it securely. It won't be shown again.",
    passwordNext: "At first sign-in they set up two-factor authentication.",
    done: "Done",
    editTitle: "Edit {{name}}",
    accountDisabled: "Account disabled",
    accountDisabledHint: "Disabled users can't sign in. Their history stays.",
    saved: "User saved",
    resetDone: "Password and 2FA reset",
    empty: "No users match these filters.",
    ownerLocked: "Only the owner can change the owner account.",
    newBot: "New bot",
    filterKind: "Type",
    allKinds: "Everyone",
    people: "People",
    bots: "Bots",
    access: "Access",
    lastActive: "Last active",
    tokenCount: "Tokens: {{count}}",
    tokens: "Tokens",
    createBotTitle: "New bot",
    createBotButton: "Create bot",
    botCreated: "Bot created",
    botIntro: "A bot is an account for an integration (CI, deploy scripts, monitoring). It has a role and projects like a person, but no password: it works only through API tokens you create for it.",
    botTokensTitle: "Tokens for {{name}}",
    botTokensIntro: "Anything using these tokens acts as @{{username}}, with its role and projects. Treat them like passwords.",
    botDisabledTokens: "This bot is disabled, so its tokens don't work until you enable it again.",
  },
  profile: {
    title: "Profile",
    account: "Account",
    displayName: "Display name",
    username: "Username",
    role: "Platform role",
    preferences: "Preferences",
    language: "Language",
    theme: "Theme",
    saved: "Profile saved",
    changePassword: "Change password",
    currentPassword: "Current password",
    newPassword: "New password",
    confirmPassword: "Repeat new password",
    passwordHint: "At least 12 characters.",
    passwordMismatch: "The passwords don't match.",
    passwordTooShort: "Use at least 12 characters.",
    passwordChanged: "Password changed",
    security: "Security",
    sessions: "Sessions",
    logoutOthers: "Log out everywhere else",
    logoutOthersHint: "Signs out every other browser and device. This one stays signed in.",
    loggedOutOthers: "Other sessions signed out",
    logout: "Log out",
  },
  notFound: {
    title: "Page not found",
    body: "The page doesn't exist, or you don't have access to it.",
    home: "Go to projects",
  },
};

export default en;

type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };
/** Every other language must provide exactly the same keys. */
export type Translation = Shape<typeof en>;
